import { Inject, Injectable, InjectionToken, OnDestroy, Optional } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { take } from 'rxjs/operators';
import {
    ITaskContextFactsDto,
    TaskCompletionServiceProxy,
    UndoTaskCompletionInput,
    UndoTaskCompletionResultDtoIHandleError,
} from '@shared/service-proxies/service-proxies';

import {
    ActionToastClock,
    BROWSER_ACTION_TOAST_CLOCK,
    ToastCountdown,
    countdownRemainingMs,
    pauseCountdown,
    resumeCountdown,
    startCountdown,
} from './action-toast-timer';

/**
 * 'lead' = the design's dark lw toast with the green check, no entry animation; 'status' = the guard-status pill
 * (`vfPop`) — plan C.9. 'plain' = the lead card without the check, for a message that is no success (P4: a failed Undo).
 */
export type ActionToastVariant = 'lead' | 'status' | 'plain';

/** The toast's one button: `Undo` (P4, FE-106), `Change` (P6). */
export interface ActionToastAction {
    readonly label: string;
    /** 'undo' draws the rotate-ccw glyph before the label (C.9). */
    readonly icon?: 'undo' | null;
    /**
     * Runs once per click. While a returned promise is pending the button is disabled and the toast stays (its timer is
     * held); when it resolves the toast closes, unless the action already showed another toast; when it rejects the
     * button comes back and the toast runs out the time it had left. A synchronous action closes the toast at once.
     * `anchor` is the clicked button's viewport box (null when unknown): `Change` opens "Follow up on" at it.
     */
    readonly run: (anchor: ActionToastAnchor | null) => void | PromiseLike<unknown>;
}

/** The toast button's viewport box (`getBoundingClientRect`) at the click. */
export interface ActionToastAnchor {
    readonly top: number;
    readonly bottom: number;
    readonly left: number;
    readonly right: number;
}

/** What `show()` takes (FE-52). */
export interface ActionToastRequest {
    readonly variant: ActionToastVariant;
    /** e.g. `Moved to Proposal sent`. A blank title shows nothing. */
    readonly title: string;
    readonly sub?: string | null;
    readonly action?: ActionToastAction | null;
    /** How long it stays while nobody holds it (`ACTION_TOAST_DURATION_MS`); an invalid value → the variant's. */
    readonly durationMs: number;
}

/** The button as the toast component draws it. */
export interface ActionToastActionVm {
    readonly label: string;
    readonly icon: 'undo' | null;
    /** The action is in flight: the button is disabled. */
    readonly busy: boolean;
}

/** What `current$` carries: the toast on screen (a new object for every change), or null. */
export interface ActionToastVm {
    /**
     * New for every `show()`. `pause`, `resume`, `runAction` and `dismiss` name it, so a late event never touches a
     * newer toast.
     */
    readonly id: number;
    readonly variant: ActionToastVariant;
    readonly title: string;
    readonly sub: string | null;
    readonly action: ActionToastActionVm | null;
}

/** Why the timer is held: the pointer is over the toast, or focus is inside it (S-13). */
export type ActionToastHoldReason = 'hover' | 'focus';

/** The C.9 timings, in milliseconds. */
export const ACTION_TOAST_DURATION_MS = {
    /** `Moved to {Stage}` (D:8826; RAIL-13), and the default of the lead variant. */
    stageMoved: 4500,
    /** `Lead follow-up completed · Undo` (PR14). */
    leadUndo: 6000,
    /** The status pill, e.g. `Task completed` (ANIM-07), and the default of the status variant. */
    status: 2600,
} as const;

/** The clock the toast timer runs on: the browser's, unless a spec provides another. */
export const ACTION_TOAST_CLOCK = new InjectionToken<ActionToastClock>('ACTION_TOAST_CLOCK', {
    providedIn: 'root',
    factory: () => BROWSER_ACTION_TOAST_CLOCK,
});

/** P4 (FE-106; C.6.3, C.8.5 #26, #27): the completion toasts' copy, verbatim. */
export const ACTION_TOAST_COPY = {
    leadFollowUpCompleted: 'Lead follow-up completed',
    undo: 'Undo',
    undoFailed: 'Could not undo the completion.',
    /** Complete anyway's status pill (D:9171: no full stop). */
    taskCompleted: 'Task completed',
} as const;

/**
 * BE-19 / PR17: the completion's `Message_Info` on the success no-op of an already completed task
 * (`TaskCompletionRefusalCodes.AlreadyCompleted`). Nothing was written, so nothing can be undone.
 */
export const COMPLETION_ALREADY_COMPLETED_INFO = 'completion-already-completed';

/** The completion answered the success no-op (PR17): no toast, no Undo, no celebration — the host refreshes the row. */
export function isAlreadyCompletedNoOp(messageInfo: string | null | undefined): boolean {
    return typeof messageInfo === 'string' && messageInfo.trim() === COMPLETION_ALREADY_COMPLETED_INFO;
}

/** What `showLeadFollowUpCompleted` takes (FE-106; C.6.3 R11: a Primary Lead Email in Follow up completed at once). */
export interface LeadFollowUpCompletedToast {
    readonly taskId: number;
    /** The task's name. Not drawn (C.6.3: no sub-line); carried on `completionUndone$`. */
    readonly title?: string | null;
    /**
     * UTC ISO instant taken just BEFORE the completion request went out (`new Date().toISOString()`): BE-39's anchor,
     * so Undo finds the follow-up row that completion resolved. Without it no Undo is offered.
     */
    readonly completedAtUtc: string | null | undefined;
    /** The completion response's `message_Info`: `completion-already-completed` (a success no-op) shows nothing. */
    readonly completionInfo?: string | null;
}

/**
 * One successful Undo (BE-39): the task is open again, its follow-up row restored with its Start / Due. The reopen's
 * SignalR card can arrive BEFORE the restore, so a host applies `facts` over whatever the card painted (B2 / B3:
 * My Day's row facts, the panel banner).
 */
export interface CompletionUndoneEvent {
    readonly taskId: number;
    readonly title: string;
    readonly reopened: boolean;
    /** The task was already open (a reopen elsewhere won the race): nothing was restored. */
    readonly wasAlreadyOpen: boolean;
    readonly followUpRestored: boolean;
    /** The task's facts after the restore; null when the server sent none. */
    readonly facts: ITaskContextFactsDto | null;
}

/** BE-39's answer → the Undo event, or null for a refusal, an empty answer or a transport failure. */
export function completionUndoneEventOf(
    res: UndoTaskCompletionResultDtoIHandleError | null | undefined,
    taskId: number,
    title: string | null | undefined
): CompletionUndoneEvent | null {
    const data = res && !res.isError ? res.responseData : undefined;
    if (!data) {
        return null;
    }
    return {
        taskId: typeof data.taskId === 'number' && data.taskId > 0 ? data.taskId : taskId,
        title: textOrNull(title) || '',
        reopened: data.reopened === true,
        wasAlreadyOpen: data.wasAlreadyOpen === true,
        followUpRestored: data.followUpRestored === true,
        facts: data.facts || null,
    };
}

type ToastHold = ActionToastHoldReason | 'action';

/**
 * The ONE global action toast (MYDAY_LEAD_COMMS_PLAN.md Appendix C.9, FE-52; S.7 I-16): the rail's `Moved to {Stage}`
 * (P3), `Lead follow-up completed · Undo` and the status pill (P4), LP's stage toast with `Change` (P6). The
 * always-mounted `vf-action-toast` in MainLayout (FE-53, FE-63) renders `current$`.
 *
 * - One toast at a time: `show()` replaces the current toast and restarts the timer (the design's `clearTimeout`).
 * - The timer pauses while the toast is hovered or focused — each hold counted on its own — and resumes with the time
 *   that was left (S-13, PR14; WCAG 2.2.1). A replaced or closed toast drops its holds.
 * - `dismiss()` closes it at once.
 * - P4 (FE-106): `showLeadFollowUpCompleted` (C.6.3: `Lead follow-up completed` + `Undo`, 6 s, BE-39 behind the
 *   button) and `showStatus` (the 2600 ms status pill: Complete anyway's `Task completed`, Shell B's saved status).
 *   A successful Undo is announced on `completionUndone$` with the restored facts.
 *
 * A typed, feature-scoped root service: no DataService channel (STANDARDS §1.12).
 */
@Injectable({ providedIn: 'root' })
export class ActionToastService implements OnDestroy {
    private readonly state = new BehaviorSubject<ActionToastVm | null>(null);
    private readonly undoneSource = new Subject<CompletionUndoneEvent>();

    /** The toast on screen, or null. Replays the current value to a late subscriber. */
    readonly current$: Observable<ActionToastVm | null> = this.state.asObservable();

    /**
     * A Lead follow-up completion was undone (BE-39): the task is open again with its follow-up restored. Hosts apply
     * `facts` over the reopen's card (B2 / B3 wire My Day and the panel banner to it).
     */
    readonly completionUndone$: Observable<CompletionUndoneEvent> = this.undoneSource.asObservable();

    private lastId = 0;
    private action: ActionToastAction | null = null;
    private countdown: ToastCountdown | null = null;
    private timer: unknown = null;
    private readonly holds = new Set<ToastHold>();

    constructor(
        @Inject(ACTION_TOAST_CLOCK) private readonly clock: ActionToastClock,
        /** BE-39's proxy; without it (a spec, a host without the API) the Lead toast offers no Undo. */
        @Optional() private readonly completions?: TaskCompletionServiceProxy | null
    ) {}

    /**
     * PR38: another Undo reopened a completed task - the Won popup's `Undo — keep Lead open` reopens the lead task the Won
     * completed (TaskLeadStage/UndoWon). Announced on `completionUndone$` as this toast's own Undo is, with the facts the
     * Undo answered: My Day applies them over the row the reopen's card brought back (its lead group at once - the card
     * carries none), and the hosts re-read the task as after any Undo. Nothing for a task without an id.
     */
    announceTaskReopened(taskId: number, followUpRestored: boolean, facts: ITaskContextFactsDto | null = null): void {
        if (typeof taskId !== 'number' || !isFinite(taskId) || taskId <= 0) {
            return;
        }
        this.undoneSource.next({ taskId, title: '', reopened: true, wasAlreadyOpen: false, followUpRestored, facts: facts || null });
    }

    /**
     * C.6.3 (R11, PR14): `Lead follow-up completed` + `Undo` on the lead toast, 6000 ms, held while hovered or focused.
     * Undo calls `TaskCompletion/UndoTaskCompletion { taskId, completedAtUtc }` with the button disabled in flight: done
     * → the toast closes and `completionUndone$` carries the restored facts; refused or failed → the toast reads
     * `Could not undo the completion.` (no button, no check). Nothing is shown for the success no-op of an already
     * completed task (PR17) or a task without an id; without an anchor or a proxy the toast has no Undo. One toast at a
     * time: it replaces the current one. Returns the toast's id, or null when nothing was shown.
     */
    showLeadFollowUpCompleted(request: LeadFollowUpCompletedToast): number | null {
        const id = request ? request.taskId : 0;
        const taskId = typeof id === 'number' && isFinite(id) && id > 0 ? id : 0;
        if (!taskId || isAlreadyCompletedNoOp(request.completionInfo)) {
            return null;
        }
        const anchor = textOrNull(request.completedAtUtc);
        const title = textOrNull(request.title) || '';
        const action: ActionToastAction | null =
            anchor && this.completions
                ? { label: ACTION_TOAST_COPY.undo, icon: 'undo', run: () => this.undoCompletion(taskId, anchor, title) }
                : null;
        return this.show({
            variant: 'lead',
            title: ACTION_TOAST_COPY.leadFollowUpCompleted,
            action,
            durationMs: ACTION_TOAST_DURATION_MS.leadUndo,
        });
    }

    /** The status pill (C.9; ANIM-07): e.g. `Task completed`, 2600 ms, `vfPop` on its card. Null for a blank title. */
    showStatus(title: string): number | null {
        return this.show({ variant: 'status', title, durationMs: ACTION_TOAST_DURATION_MS.status });
    }

    /** Shows a toast in place of the current one and returns its id; null (nothing changes) for a blank title. */
    show(request: ActionToastRequest): number | null {
        const title = textOrNull(request ? request.title : null);
        if (!title) {
            return null;
        }
        this.clearTimer();
        this.holds.clear();
        const action = request.action || null;
        const label = action ? textOrNull(action.label) : null;
        this.action = label ? action : null;
        const id = ++this.lastId;
        this.countdown = startCountdown(durationOf(request), this.clock.now());
        this.state.next({
            id,
            variant: request.variant,
            title,
            sub: textOrNull(request.sub),
            action: action && label ? { label, icon: action.icon === 'undo' ? 'undo' : null, busy: false } : null,
        });
        this.arm(id);
        return id;
    }

    /** Closes the current toast now — or, given an id, only if that toast is still the current one. */
    dismiss(id?: number): void {
        const current = this.state.value;
        if (current && (id === undefined || id === current.id)) {
            this.close();
        }
    }

    /** The pointer entered the toast or focus moved into it: hold the timer. */
    pause(id: number, reason: ActionToastHoldReason): void {
        if (this.isCurrent(id)) {
            this.hold(reason);
        }
    }

    /** The pointer left the toast or focus moved out of it: once nothing holds it, run out the time that was left. */
    resume(id: number, reason: ActionToastHoldReason): void {
        if (this.isCurrent(id)) {
            this.release(id, reason);
        }
    }

    /** The toast's button was clicked: runs its action once (see `ActionToastAction.run`), with the button's box. */
    runAction(id: number, anchor: ActionToastAnchor | null = null): void {
        const current = this.state.value;
        const action = this.action;
        if (!current || current.id !== id || !action || !current.action || current.action.busy) {
            return;
        }
        this.setBusy(true);
        this.hold('action');
        let result: void | PromiseLike<unknown>;
        try {
            result = action.run(anchor);
        } catch (error) {
            this.actionFailed(id);
            throw error;
        }
        if (isThenable(result)) {
            result.then(
                () => this.actionDone(id),
                () => this.actionFailed(id)
            );
        } else {
            this.actionDone(id);
        }
    }

    ngOnDestroy(): void {
        this.clearTimer();
        this.holds.clear();
        this.action = null;
        this.countdown = null;
        this.state.complete();
        this.undoneSource.complete();
    }

    /**
     * The Undo button's work: one BE-39 call. Always resolves (the toast's action contract): on success it announces
     * the restored task, and the toast then closes; on a refusal or a failure it first shows the failure toast, which
     * the toast keeps up (an action that showed another toast leaves that one).
     */
    private undoCompletion(taskId: number, completedAtUtc: string, title: string): Promise<void> {
        const proxy = this.completions as TaskCompletionServiceProxy;
        return new Promise<void>((resolve) => {
            let settled = false;
            const finish = (res: UndoTaskCompletionResultDtoIHandleError | null): void => {
                if (settled) {
                    return;
                }
                settled = true;
                const event = completionUndoneEventOf(res, taskId, title);
                if (event) {
                    this.undoneSource.next(event);
                } else {
                    this.show({
                        variant: 'plain',
                        title: ACTION_TOAST_COPY.undoFailed,
                        durationMs: ACTION_TOAST_DURATION_MS.stageMoved,
                    });
                }
                resolve();
            };
            proxy
                .undoTaskCompletion(new UndoTaskCompletionInput({ taskId, completedAtUtc }))
                .pipe(take(1))
                .subscribe({
                    next: (res) => finish(res),
                    error: () => finish(null),
                    complete: () => finish(null),
                });
        });
    }

    private actionDone(id: number): void {
        if (this.isCurrent(id)) {
            this.close();
        }
    }

    private actionFailed(id: number): void {
        if (this.isCurrent(id)) {
            this.setBusy(false);
            this.release(id, 'action');
        }
    }

    private hold(reason: ToastHold): void {
        if (this.holds.has(reason)) {
            return;
        }
        if (this.holds.size === 0 && this.countdown) {
            this.countdown = pauseCountdown(this.countdown, this.clock.now());
            this.clearTimer();
        }
        this.holds.add(reason);
    }

    private release(id: number, reason: ToastHold): void {
        if (!this.holds.delete(reason) || this.holds.size > 0 || !this.countdown) {
            return;
        }
        this.countdown = resumeCountdown(this.countdown, this.clock.now());
        this.arm(id);
    }

    /** One timer for what the current toast has left. */
    private arm(id: number): void {
        this.clearTimer();
        if (!this.countdown) {
            return;
        }
        this.timer = this.clock.setTimer(() => {
            this.timer = null;
            if (this.isCurrent(id)) {
                this.close();
            }
        }, countdownRemainingMs(this.countdown, this.clock.now()));
    }

    private close(): void {
        this.clearTimer();
        this.holds.clear();
        this.action = null;
        this.countdown = null;
        if (this.state.value !== null) {
            this.state.next(null);
        }
    }

    private clearTimer(): void {
        if (this.timer !== null) {
            this.clock.clearTimer(this.timer);
            this.timer = null;
        }
    }

    private setBusy(busy: boolean): void {
        const current = this.state.value;
        if (current && current.action && current.action.busy !== busy) {
            this.state.next({ ...current, action: { ...current.action, busy } });
        }
    }

    private isCurrent(id: number): boolean {
        const current = this.state.value;
        return !!current && current.id === id;
    }
}

function durationOf(request: ActionToastRequest): number {
    const ms = request.durationMs;
    if (typeof ms === 'number' && isFinite(ms) && ms > 0) {
        return ms;
    }
    return request.variant === 'status' ? ACTION_TOAST_DURATION_MS.status : ACTION_TOAST_DURATION_MS.stageMoved;
}

function textOrNull(value: string | null | undefined): string | null {
    return typeof value === 'string' && value.trim() !== '' ? value.trim() : null;
}

function isThenable(value: unknown): value is PromiseLike<unknown> {
    return !!value && typeof (value as PromiseLike<unknown>).then === 'function';
}
